import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Card, TextField, Alert, CircularProgress, Chip, Accordion, AccordionSummary, AccordionDetails, Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { Wifi, AlertCircle, CheckCircle2, Lock, Send, RefreshCw } from "lucide-react";
import { useToastContext } from "../context/ToastContext";
import { useWifiVerification } from "../hooks/useWifiVerification";
import type { CwmpDeviceParamsResponse, CwmpWlanParams, CwmpInterfaceConfig } from "../hooks/useWifiVerification";
import { WifiConfigurationPanel } from "./WifiConfigurationPanel";

interface WifiSettingsProps {
  serviceId: string;
}

export default function WifiSettings({ serviceId }: WifiSettingsProps) {
  const { error, success } = useToastContext();
  const {
    isVerified,
    verificationCode,
    isLoadingVerification,
    isLoadingSendCode,
    isTokenExpired,
    sendCodeBlockRemaining,
    setVerificationCode,
    initiateSendCode,
    sendVerificationCode,
    getCwmpDeviceParams,
    setCwmpDeviceParams,
    clearVerification,
    getToken,
    validateServiceId,
  } = useWifiVerification(serviceId);

  // Estado para controlar si mostrar el modal de código
  const [showCodeModal, setShowCodeModal] = useState(false);

  // Estado para loading del botón actualizar
  const [isUpdating, setIsUpdating] = useState(false);

  // Estado para loading del botón guardar
  const [isSaving, setIsSaving] = useState(false);

  // Estado para errores de validación en WifiConfigurationPanel
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Estado para el countdown del botón de reenvío
  const [resendCountdown, setResendCountdown] = useState(0);

  // Estado para el countdown del código de seguridad (codeExpire)
  const [codeExpireAt, setCodeExpireAt] = useState<number | null>(null);
  const [codeCountdown, setCodeCountdown] = useState<string>("00:00");

  // Estado para la expiración de la sesión (expireAT)
  const [sessionExpireAt, setSessionExpireAt] = useState<number | null>(null);
  const [sessionCountdown, setSessionCountdown] = useState<string>("00:00");

  // Estado para loading de los parámetros CWMP
  const [isFetchingCwmpParams, setIsFetchingCwmpParams] = useState(false);

  const formatCountdown = (totalSeconds: number) => {
    const safeSeconds = Math.max(0, Math.floor(totalSeconds));
    const hours = Math.floor(safeSeconds / 3600);
    const minutes = Math.floor((safeSeconds % 3600) / 60);
    const seconds = safeSeconds % 60;

    if (hours > 0) {
      const hourLabel = hours === 1 ? "hora" : "horas";
      const minuteLabel = minutes === 1 ? "min" : "min";

      if (minutes === 0 && seconds === 0) {
        return `${hours} ${hourLabel}`;
      }

      if (seconds === 0) {
        return `${hours} ${hourLabel}${minutes > 0 ? ` ${minutes} ${minuteLabel}` : ""}`;
      }

      return `${hours} ${hourLabel}${minutes > 0 ? ` ${minutes} ${minuteLabel}` : ""} ${seconds} s`;
    }

    return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  };

  // Estado para guardar los datos CWMP
  const [cwmpDeviceParams, updateCwmpDeviceParams] = useState<CwmpDeviceParamsResponse | null>(null);

  // Estado para parámetros WiFi editados
  const [editedWifiParams, setEditedWifiParams] = useState<CwmpWlanParams | null>(null);

  // Estado para los cambios registrados (solo los campos modificados)
  const [changedParams, setChangedParams] = useState<{ [key: string]: any }>({});

  // Estado para el modal de resultado (éxito/error)
  const [resultModal, setResultModal] = useState<{
    open: boolean;
    type: "success" | "error";
    message: string;
    savedParams?: {
      ssid2G?: string;
      password2G?: string;
      ssid5G?: string;
      password5G?: string;
    };
  }>({
    open: false,
    type: "success",
    message: "",
  });

  // Validar que el serviceId coincida con el token guardado
  useEffect(() => {
    const isValid = validateServiceId(serviceId);
    if (!isValid && isVerified) {
      // El serviceId cambió, limpiar la verificación
      clearVerification();
    }
  }, [serviceId, isVerified, validateServiceId, clearVerification, error]);

  // Cargar expireSession y codeExpire del localStorage al montar
  useEffect(() => {
    const WIFI_TOKEN_STORAGE_KEY_PREFIX = "wifi_";
    const CWMP_STORAGE_KEY_PREFIX = "cwmp_";
    const tokenKey = `${WIFI_TOKEN_STORAGE_KEY_PREFIX}${serviceId}`;
    const cwmpKey = `${CWMP_STORAGE_KEY_PREFIX}${serviceId}`;

    const storedData = localStorage.getItem(tokenKey);

    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.codeExpire) {
          setCodeExpireAt(parsedData.codeExpire);
        }
        if (parsedData.expireSession) {
          setSessionExpireAt(parsedData.expireSession);
        }
      } catch (error) {
        console.error("Error loading security data from localStorage:", error);
      }
    }

    // Cargar datos CWMP del localStorage si el token es válido
    if (isVerified && !isTokenExpired) {
      const storedCwmpData = localStorage.getItem(cwmpKey);
      if (storedCwmpData) {
        try {
          const parsedCwmpData = JSON.parse(storedCwmpData);
          updateCwmpDeviceParams(parsedCwmpData);
        } catch (error) {
          console.error("Error loading CWMP data from localStorage:", error);
        }
      }
    }
  }, [serviceId, isVerified, isTokenExpired]);

  // Efecto para detectar cambios de pestaña y verificar token
  useEffect(() => {
    const handleVisibilityChange = () => {
      // Si la pestaña se vuelve visible
      if (!document.hidden && isVerified) {
        // Verificar si el token expiró
        if (isTokenExpired) {
          // Token expirado, limpiar datos
          updateCwmpDeviceParams(null);
          setEditedWifiParams(null);
          setChangedParams({});
          clearVerification();
          error("Tu sesión ha expirado 😕 \n Verifica tu identidad nuevamente y listo 🔐");
        }
        // Si el token no expiró, mantener los datos en caché (cwmpDeviceParams)
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [isVerified, isTokenExpired, clearVerification, error]);

  // Efecto para manejar el countdown del código de seguridad
  useEffect(() => {
    if (!codeExpireAt) return;

    const updateCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, codeExpireAt - now);

      if (remaining <= 0) {
        setCodeCountdown("00:00");
        setCodeExpireAt(null);
        setShowCodeModal(false);
        error("El código expiró ⏰ \n Pide uno nuevo y sigue adelante 😊");
        return;
      }

      setCodeCountdown(formatCountdown(Math.floor(remaining / 1000)));
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [codeExpireAt, error]);

  // Efecto para manejar el countdown de la sesión
  useEffect(() => {
    if (!sessionExpireAt) return;

    const updateSessionCountdown = () => {
      const now = Date.now();
      const remaining = Math.max(0, sessionExpireAt - now);

      if (remaining <= 0) {
        setSessionCountdown("00:00");
        setSessionExpireAt(null);
        error("Tu sesión venció 😕 \n Verifica de nuevo y continuamos 🔐");
        clearVerification();
        return;
      }

      setSessionCountdown(formatCountdown(Math.floor(remaining / 1000)));
    };

    updateSessionCountdown();
    const interval = setInterval(updateSessionCountdown, 1000);
    return () => clearInterval(interval);
  }, [sessionExpireAt, error, clearVerification]);

  // Efecto para manejar el countdown
  useEffect(() => {
    if (resendCountdown <= 0) return;

    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [resendCountdown]);

  // Manejar solicitud de código (con soporte para Alt+Click para enviar wpp:false)
  const handleRequestSecurityCode = async (event?: React.MouseEvent<HTMLButtonElement>) => {
    const isBlocked = sendCodeBlockRemaining > 0;

    // Si se presiona Alt + Click, enviar con wpp:false
    const isAltClick = event?.altKey;

    // Siempre consultar el backend: cuando está bloqueado usamos skipLocalCheck para
    // obtener los tiempos actualizados sin enviar un nuevo código.
    const success_result = await initiateSendCode(serviceId, isAltClick, isBlocked);

    if (success_result.success || isBlocked) {
      setVerificationCode(""); // Limpiar el código antes de abrir el modal
      setShowCodeModal(true);

      if (!isBlocked) {
        success("¡Código enviado! 📲 \n Revisa tu WhatsApp 😊");
      }

      // Actualizar tiempos con los valores frescos del backend (siempre, incluso si está bloqueado)
      if (success_result.codeExpire) {
        setCodeExpireAt(success_result.codeExpire);
        setResendCountdown(Math.max(0, Math.ceil((success_result.codeExpire - Date.now()) / 1000)));
      }
      if (success_result.expireSession) {
        setSessionExpireAt(success_result.expireSession);
      }
    } else {
      error(success_result.message || "Oops, algo salió mal 😅 \n Intenta nuevamente, por favor");
    }
  };

  // Fase 1: Enviar código de verificación
  const handleSendVerificationCode = async () => {
    if (!verificationCode.trim()) {
      error("Ingresa el código que recibiste 📝 \n Debe tener 6 dígitos");
      return;
    }

    try {
      const result = await sendVerificationCode(serviceId, verificationCode);
      if (result.success) {
        success("¡Eres tú! ✅ \n Identidad verificada correctamente");
        // Cerrar el modal después de verificación exitosa
        setShowCodeModal(false);
        // Limpiar el countdown del código
        setCodeExpireAt(null);
        setCodeCountdown("00:00");
        // NO limpiar sessionExpireAt - la sesión sigue activa
        // Actualizar localStorage: eliminar codeExpire pero mantener expireSession
        const WIFI_TOKEN_STORAGE_KEY_PREFIX = "wifi_";
        const tokenKey = `${WIFI_TOKEN_STORAGE_KEY_PREFIX}${serviceId}`;
        const existingData = localStorage.getItem(tokenKey);
        if (existingData) {
          try {
            const parsedData = JSON.parse(existingData);
            // Mantener expireSession pero eliminar codeExpire
            const updatedData = {
              ...parsedData,
              codeExpire: undefined,
            };
            localStorage.setItem(tokenKey, JSON.stringify(updatedData));
          } catch (e) {
            console.error("Error updating localStorage:", e);
          }
        }
        // Iniciar contador de 30 segundos
        setResendCountdown(30);
        // Llamar a getCwmpDeviceParams después de verificación exitosa con el token retornado
        if (result.token) {
          setIsFetchingCwmpParams(true);
          try {
            const cwmpResult = await getCwmpDeviceParams(serviceId, result.token);
            if (cwmpResult?.tokenInvalid) {
              error("El token no es válido 🔑 \n Pide un nuevo código, ¡no te preocupes!");
              setResendCountdown(0);
            } else if (cwmpResult?.message) {
              // Mostrar mensaje de error específico del servidor
              error(cwmpResult.message);
            } else if (cwmpResult) {
              success("¡Parámetros listos! 🎉 \n Tu dispositivo está configurado");
              updateCwmpDeviceParams(cwmpResult);
            }
          } catch (cwmpError) {
            console.error("Error fetching CWMP params:", cwmpError);
            error("Hmm, no pudimos obtener los parámetros 😕 \n Intenta de nuevo en un momento");
          } finally {
            setIsFetchingCwmpParams(false);
          }
        }
      } else {
        error(result.message || "El código no es correcto 🤔 \n Verifica los dígitos e intenta de nuevo");
        setVerificationCode(""); // Limpiar el input en caso de error
      }
    } catch (catchError) {
      console.error("Error sending verification code:", catchError);
      error("No pudimos enviar el código 😢 \n Intenta nuevamente en unos segundos");
      setVerificationCode(""); // Limpiar el input en caso de error
    }
  };

  // Función para actualizar parámetros del dispositivo
  const handleUpdateDeviceParams = async () => {
    if (isTokenExpired) {
      error("Tu sesión venció 😕 \n Verifica de nuevo para continuar 🔐");
      clearVerification();
      return;
    }

    try {
      setIsUpdating(true);
      // Limpiar datos actuales y cambios locales
      updateCwmpDeviceParams(null);
      setEditedWifiParams(null);
      setChangedParams({});

      const result = await getCwmpDeviceParams(serviceId, getToken() || "");

      // Verificar si el token es inválido
      if (result?.tokenInvalid) {
        error("Ese token no es válido 🔑 \n Pide uno nuevo y todo estará bien 👍");
        setResendCountdown(0); // Cancelar el countdown
        return;
      }

      if (result?.message) {
        // Mostrar mensaje de error específico del servidor
        error(result.message);
        return;
      }

      if (result) {
        success("¡Actualizado! 🔄 \n Tu dispositivo está al día");
        updateCwmpDeviceParams(result);
      } else {
        error("Error al actualizar los parámetros del dispositivo 😔  \n tranquilo esto a veces puede pasar, solo intenta nuevamente y listo 😉");
      }
    } catch (catchError) {
      console.error("Error updating device params:", catchError);
      error("Algo no salió del todo 😔 \n Intenta de nuevo y listo 😊");
    } finally {
      setIsUpdating(false);
    }
  };

  // Manejar cambios en los parámetros WiFi
  const handleWifiParamsChange = (updatedParams: CwmpWlanParams) => {
    setEditedWifiParams(updatedParams);
  };

  // Manejar cambios registrados (solo los campos modificados)
  const handleWifiChangesUpdate = (changes: { [key: string]: any }) => {
    setChangedParams(changes);
  };

  // Guardar cambios de parámetros WiFi
  const handleSaveWifiParams = async () => {
    // Verificar primero si hay errores de validación
    if (hasValidationErrors) {
      error("No puedes guardar cambios mientras haya errores de validación. Por favor, verififica todos los campos.");
      return;
    }

    if (Object.keys(changedParams).length === 0) {
      error("No hay cambios para guardar 🤷 \n Modifica algo y vuelve a intentar 😊");
      return;
    }

    if (isTokenExpired) {
      error("Tu sesión expiró 😕 \n Verifica tu identidad de nuevo para continuar 🔐");
      clearVerification();
      return;
    }

    try {
      setIsSaving(true);
      const token = getToken();

      if (!token) {
        error("Hmm, no tenemos el token 🔑 \n Intenta de nuevo, por favor 🙋");
        return;
      }

      // Preparar parámetros para guardar: agregar "-5G" a SSID de 5GHz solo aquí
      const paramsToSave = { ...changedParams };
      Object.entries(paramsToSave).forEach(([key, value]) => {
        if (key.includes("WLANConfiguration.5.SSID")) {
          const actualValue = typeof value === "object" && value !== null && "_value" in value ? (value as any)._value : value;
          // Agregar "-5G" solo si no lo tiene ya
          if (!actualValue?.toString().endsWith("-5G")) {
            const valueWithSuffix = { ...value, _value: actualValue + "-5G" };
            paramsToSave[key] = valueWithSuffix;
          }
        }
      });

      const result = await setCwmpDeviceParams(serviceId, token, paramsToSave);

      if (result.tokenInvalid) {
        error("El token ya no es válido 🔑 \n Pide uno nuevo y continuaremos 🙋");
        setResendCountdown(0);
        return;
      }

      if (result.success) {
        // Extraer parámetros guardados para mostrar en el modal
        const savedParams: {
          ssid2G?: string;
          password2G?: string;
          ssid5G?: string;
          password5G?: string;
        } = {};

        Object.entries(paramsToSave).forEach(([key, value]) => {
          const actualValue = typeof value === "object" && value !== null && "_value" in value ? (value as any)._value : value;
          if (key.includes("WLANConfiguration.1.SSID")) {
            savedParams.ssid2G = actualValue as string;
          } else if (key.includes("WLANConfiguration.1.PreSharedKey")) {
            savedParams.password2G = actualValue as string;
          } else if (key.includes("WLANConfiguration.5.SSID")) {
            // Mostrar como se envió a la API (con "-5G")
            savedParams.ssid5G = actualValue as string;
          } else if (key.includes("WLANConfiguration.5.PreSharedKey")) {
            savedParams.password5G = actualValue as string;
          }
        });

        // Mostrar modal de éxito
        setResultModal({
          open: true,
          type: "success",
          message: result.message || "Parámetros WiFi guardados correctamente",
          savedParams,
        });
        // Actualizar los parámetros mostrados con los nuevos valores
        if (cwmpDeviceParams && cwmpDeviceParams.params) {
          updateCwmpDeviceParams({
            ...cwmpDeviceParams,
            params: {
              params: { ...cwmpDeviceParams.params.params, ...changedParams },
              interface: cwmpDeviceParams.params.interface || {},
            },
          });
        }
        // Limpiar los cambios
        setChangedParams({});
        setEditedWifiParams(null);
      } else {
        // Mostrar modal de error
        setResultModal({
          open: true,
          type: "error",
          message: result.message || "Error al guardar los parámetros WiFi",
        });
      }
    } catch (catchError) {
      console.error("Error saving WiFi params:", catchError);
      // Mostrar modal de error
      setResultModal({
        open: true,
        type: "error",
        message: "Algo salió mal 😔 \n Intenta de nuevo en unos momentos ⏰",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: { xs: 2, sm: 2.5, md: 3 } }}>
      {/* Loading Overlay - Obteniendo parámetros CWMP */}
      {isFetchingCwmpParams && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1400,
            flexDirection: "column",
            gap: 2,
          }}
        >
          <CircularProgress size={60} sx={{ color: "white" }} />
          <Typography
            sx={{
              color: "white",
              fontWeight: 600,
              fontSize: "1rem",
              textAlign: "center",
            }}
          >
            Obteniendo parámetros del dispositivo...
          </Typography>
        </Box>
      )}

      {/* Token Expirado - Alert */}
      {isTokenExpired && isVerified === false && (
        <Alert
          severity="error"
          sx={{
            backgroundColor: "hsl(0 84.2% 97%)",
            border: "2px solid hsl(0 84.2% 70%)",
            borderRadius: "14px",
            padding: { xs: "12px", sm: "14px", md: "16px" },
            "& .MuiAlert-icon": {
              color: "hsl(0 84.2% 60.2%)",
            },
          }}
          icon={
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 24,
                height: 24,
              }}
            >
              <AlertCircle size={24} color="hsl(0 84.2% 60.2%)" />
            </Box>
          }
        >
          <Typography
            variant="body2"
            sx={{
              color: "hsl(0 84.2% 40%)",
              fontWeight: 600,
            }}
          >
            Tu sesión de WiFi ha expirado. Por favor, verifica tu identidad nuevamente.
          </Typography>
        </Alert>
      )}

      {/* Estado no verificado: Mostrar botón para pedir código */}
      {!isVerified ? (
        <>
          {/* Alert de información */}
          <Alert
            severity="info"
            sx={{
              backgroundColor: "hsl(224.3 76.3% 97%)",
              border: "2px solid hsl(224.3 76.3% 70%)",
              borderRadius: "14px",
              padding: { xs: "12px", sm: "14px", md: "16px" },
              "& .MuiAlert-icon": {
                color: "hsl(224.3 76.3% 45%)",
              },
            }}
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                }}
              >
                <Lock size={24} color="hsl(224.3 76.3% 45%)" />
              </Box>
            }
          >
            <Typography
              variant="body2"
              sx={{
                color: "hsl(224.3 76.3% 30%)",
                fontWeight: 600,
              }}
            >
              Para acceder a la configuración de WiFi, necesitamos verificar tu identidad.
            </Typography>
          </Alert>

          {/* Card con botón */}
          <Card
            sx={{
              width: "100%",
              p: { xs: 2.5, sm: 3, md: 4 },
              backgroundColor: "hsl(0 0% 100%)",
              border: "1px solid hsl(240 4.8% 95.9%)",
              borderRadius: "16px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 64,
                height: 64,
                backgroundColor: "hsl(224.3 76.3% 95%)",
                borderRadius: "14px",
                mb: 3,
                mx: "auto",
              }}
            >
              <Wifi size={32} color="hsl(224.3 76.3% 48%)" />
            </Box>

            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "hsl(240 10% 3.9%)",
                mb: 2,
              }}
            >
              Configuración de WiFi
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "hsl(240 3.8% 46.1%)",
                mb: 4,
                lineHeight: 1.6,
              }}
            >
              Presiona el botón de abajo para solicitar un código de seguridad. Te lo enviaremos por Whatsapp.
            </Typography>

            <Button
              fullWidth
              variant="contained"
              onClick={handleRequestSecurityCode}
              disabled={isLoadingSendCode}
              startIcon={
                isLoadingSendCode ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <Send size={18} />
                )
              }
              sx={{
                backgroundColor: "hsl(224.3 76.3% 48%)",
                color: "white",
                textTransform: "none",
                fontWeight: 600,
                py: 1.5,
                fontSize: "0.95rem",
                "&:hover": {
                  backgroundColor: "hsl(224.3 76.3% 40%)",
                },
                "&:disabled": {
                  backgroundColor: "hsl(240 3.8% 74%)",
                  color: "white",
                },
              }}
            >
              {isLoadingSendCode
                ? "Enviando..."
                : sendCodeBlockRemaining > 0
                  ? `Ingresar código (${formatCountdown(sendCodeBlockRemaining)})`
                  : resendCountdown > 0
                    ? `Pedir código (${resendCountdown}s)`
                    : "Pedir código de seguridad"}
            </Button>
          </Card>

          {/* Modal para ingresar código */}
          {showCodeModal && (
            <Box
              sx={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1300,
                p: 2,
              }}
              onClick={() => {
                if (!isLoadingVerification) {
                  setShowCodeModal(false);
                  // Mantener sessionExpireAt activo - la sesión sigue siendo válida
                }
              }}
            >
              <Card
                sx={{
                  p: { xs: 3, sm: 4 },
                  backgroundColor: "hsl(0 0% 100%)",
                  borderRadius: "16px",
                  maxWidth: 420,
                  width: "100%",
                  boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 56,
                    height: 56,
                    backgroundColor: "hsl(224.3 76.3% 95%)",
                    borderRadius: "12px",
                    mb: 3,
                    mx: "auto",
                  }}
                >
                  <Lock size={28} color="hsl(224.3 76.3% 48%)" />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "hsl(240 10% 3.9%)",
                    mb: 1,
                    textAlign: "center",
                  }}
                >
                  Código de Seguridad
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "hsl(240 3.8% 46.1%)",
                    mb: 3,
                    textAlign: "center",
                    lineHeight: 1.6,
                  }}
                >
                  Ingresa el código de 6 dígitos que recibiste por Whatsapp.
                </Typography>

                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    borderRadius: "12px",
                    alignItems: "center",
                    backgroundColor: "hsl(224.3 76.3% 97%)",
                    border: "1px solid hsl(224.3 76.3% 85%)",
                    color: "hsl(224.3 76.3% 35%)",
                    "& .MuiAlert-icon": {
                      color: "hsl(224.3 76.3% 48%)",
                    },
                  }}
                >
                  {sendCodeBlockRemaining > 0
                    ? `Escribe el código que recibiste, recuerda que puedes usar el código anterior mientras esté activo`
                    : "Escribe el código que recibiste. Si necesitas pedir otro código, podrás hacerlo cuando termine el conteo."}
                </Alert>

                {/* Countdown de expiración del código */}
                {codeExpireAt && (
                  <Box
                    sx={{
                      mb: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    <Chip
                      label={`Código expira en: ${codeCountdown}`}
                      size="small"
                      sx={{
                        backgroundColor: codeCountdown === "00:00" ? "hsl(0 84.2% 70%)" : "hsl(224.3 76.3% 95%)",
                        color: codeCountdown === "00:00" ? "white" : "hsl(224.3 76.3% 48%)",
                        fontWeight: 700,
                        height: "32px",
                        fontSize: "0.85rem",
                      }}
                    />
                  </Box>
                )}

                {/* Input del código */}
                <TextField
                  fullWidth
                  placeholder="000000"
                  type="text"
                  value={verificationCode}
                  onChange={(e) => {
                    // Solo permitir números
                    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                    setVerificationCode(value);
                  }}
                  onKeyDown={(e) => {
                    // Verificar el código al presionar Enter
                    if (e.key === "Enter" && verificationCode.length === 6 && !isLoadingVerification) {
                      handleSendVerificationCode();
                    }
                  }}
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      letterSpacing: "0.5rem",
                      fontWeight: 600,
                      fontFamily: "monospace",
                    },
                  }}
                  sx={{
                    mb: 3,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px",
                      backgroundColor: "hsl(240 4.8% 97.9%)",
                      border: "2px solid hsl(240 4.8% 95.9%)",
                      fontSize: "1.5rem",
                      "&:hover": {
                        border: "2px solid hsl(224.3 76.3% 85%)",
                      },
                      "&.Mui-focused": {
                        border: "2px solid hsl(224.3 76.3% 48%)",
                        backgroundColor: "hsl(0 0% 100%)",
                      },
                    },
                  }}
                  disabled={isLoadingVerification}
                  autoFocus
                />

                {/* Botones */}
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setShowCodeModal(false);
                      // Mantener sessionExpireAt activo - la sesión sigue siendo válida
                    }}
                    disabled={isLoadingVerification}
                    sx={{
                      borderColor: "hsl(240 3.8% 74%)",
                      color: "hsl(240 3.8% 46.1%)",
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1,
                      "&:hover": {
                        borderColor: "hsl(240 3.8% 46.1%)",
                        backgroundColor: "hsl(240 4.8% 97.9%)",
                      },
                      "&:disabled": {
                        borderColor: "hsl(240 3.8% 74%)",
                        color: "hsl(240 3.8% 74%)",
                      },
                    }}
                  >
                    Cancelar
                  </Button>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSendVerificationCode}
                    disabled={
                      isLoadingVerification || verificationCode.length < 6
                    }
                    startIcon={
                      isLoadingVerification ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <Send size={18} />
                      )
                    }
                    sx={{
                      backgroundColor: "hsl(224.3 76.3% 48%)",
                      color: "white",
                      textTransform: "none",
                      fontWeight: 600,
                      py: 1,
                      "&:hover": {
                        backgroundColor: "hsl(224.3 76.3% 40%)",
                      },
                      "&:disabled": {
                        backgroundColor: "hsl(240 3.8% 74%)",
                        color: "white",
                      },
                    }}
                  >
                    {isLoadingVerification ? "Verificando..." : "Verificar"}
                  </Button>
                </Box>

                {/* Link para re-enviar código */}
                <Typography
                  variant="caption"
                  sx={{
                    color: resendCountdown > 0 || sendCodeBlockRemaining > 0 ? "hsl(240 3.8% 74%)" : "hsl(224.3 76.3% 48%)",
                    display: "block",
                    textAlign: "center",
                    mt: 2,
                    cursor: resendCountdown > 0 || sendCodeBlockRemaining > 0 ? "not-allowed" : "pointer",
                    fontWeight: 600,
                    "&:hover": {
                      textDecoration: resendCountdown > 0 || sendCodeBlockRemaining > 0 ? "none" : "underline",
                    },
                  }}
                  onClick={() => {
                    if (resendCountdown === 0 && sendCodeBlockRemaining === 0) {
                      handleRequestSecurityCode();
                    }
                  }}
                >
                  {sendCodeBlockRemaining > 0
                    ? `Solicitud bloqueada por ${formatCountdown(sendCodeBlockRemaining)}`
                    : resendCountdown > 0
                      ? `Re-enviar en ${resendCountdown}s`
                      : "¿No recibiste el código? Re-enviar"}
                </Typography>
              </Card>
            </Box>
          )}
        </>
      ) : (
        <>
          {/* Estado verificado: Mostrar opciones WiFi */}
          <Alert
            severity="success"
            sx={{
              backgroundColor: "hsl(142 71.8% 97%)",
              border: "2px solid hsl(142 71.8% 70%)",
              borderRadius: "14px",
              padding: "16px",
              "& .MuiAlert-icon": {
                color: "hsl(142 71.8% 45%)",
              },
            }}
            icon={
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  height: 24,
                }}
              >
                <CheckCircle2 size={24} color="hsl(142 71.8% 45%)" />
              </Box>
            }
          >
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "hsl(142 71.8% 30%)",
                  fontWeight: 600,
                }}
              >
                Identidad verificada correctamente
              </Typography>
              {sessionExpireAt && (
                <Chip
                  label={`Expira en ${sessionCountdown}`}
                  size="small"
                  sx={{
                    backgroundColor: "hsl(142 71.8% 45%)",
                    color: "white",
                    fontWeight: 700,
                    height: "24px",
                  }}
                />
              )}
            </Box>
          </Alert>

          {/* Card principal de WiFi */}
          <Card
            sx={{
              width: { xs: "100vw", sm: "100%" },
              maxWidth: { xs: "100vw", sm: "100%" },
              marginLeft: { xs: "calc(-50vw + 50%)", sm: 0 },
              p: { xs: 2.5, sm: 3, md: 4 },
              backgroundColor: "hsl(0 0% 100%)",
              border: "1px solid hsl(240 4.8% 95.9%)",
              borderRadius: { xs: 0, sm: "16px" },
            }}
          >
            {/* Header con icono WiFi */}
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                alignItems: { xs: "flex-start", sm: "flex-start" },
                justifyContent: "space-between",
                gap: { xs: 2, sm: 3 },
                mb: 4,
                pb: 3,
                borderBottom: "1px solid hsl(240 4.8% 95.9%)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2.5,
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 48,
                    height: 48,
                    backgroundColor: "hsl(224.3 76.3% 95%)",
                    borderRadius: "12px",
                    flexShrink: 0,
                  }}
                >
                  <Wifi size={24} color="hsl(224.3 76.3% 48%)" />
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", mb: 0.5 }}>
                    Configuración de WiFi
                  </Typography>
                  <Typography variant="caption" sx={{ color: "hsl(240 3.8% 46.1%)" }}>
                    Gestiona tu router y conexión
                  </Typography>
                </Box>
              </Box>
              <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" }, width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    if (Object.keys(changedParams).length === 0) return;
                    handleSaveWifiParams();
                  }}
                  disabled={isSaving || isTokenExpired || hasValidationErrors || Object.keys(changedParams).length === 0}
                  title={hasValidationErrors ? "Por favor, corrige los errores de validación antes de guardar" : ""}
                  startIcon={
                    isSaving ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Send size={16} />
                    )
                  }
                  sx={{
                    backgroundColor: hasValidationErrors ? "hsl(0 84% 60%)" : "hsl(120 84.6% 45.3%)",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    width: { xs: "100%", sm: "auto" },
                    opacity: hasValidationErrors ? 0.6 : 1,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      backgroundColor: hasValidationErrors ? "hsl(0 84% 50%)" : "hsl(120 84.6% 35%)",
                    },
                    "&:disabled": {
                      backgroundColor: hasValidationErrors ? "hsl(0 84% 60%)" : "hsl(240 3.8% 74%)",
                      color: "white",
                    },
                  }}
                >
                  {isSaving ? "Guardando..." : hasValidationErrors ? "Errores en validación" : "Guardar cambios"}
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleUpdateDeviceParams}
                  disabled={isUpdating || isTokenExpired}
                  startIcon={
                    isUpdating ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <RefreshCw size={16} />
                    )
                  }
                  sx={{
                    backgroundColor: "hsl(224.3 76.3% 48%)",
                    color: "white",
                    textTransform: "none",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    width: { xs: "100%", sm: "auto" },
                    "&:hover": {
                      backgroundColor: "hsl(224.3 76.3% 40%)",
                    },
                    "&:disabled": {
                      backgroundColor: "hsl(240 3.8% 74%)",
                      color: "white",
                    },
                  }}
                >
                  {isUpdating ? "Actualizando..." : "Actualizar"}
                </Button>
              </Box>
            </Box>

            {/* Paneles de configuración detallada */}
            {cwmpDeviceParams && cwmpDeviceParams.model && cwmpDeviceParams.params && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
                {/* Configuración WiFi */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <WifiConfigurationPanel
                    model={cwmpDeviceParams.model}
                    params={editedWifiParams || cwmpDeviceParams.params.params}
                    onParamsChange={handleWifiParamsChange}
                    onChangesUpdate={handleWifiChangesUpdate}
                    onValidationErrorsChange={setHasValidationErrors}
                    capabilities={cwmpDeviceParams.capabilities}
                  />
                </Box>

                {/* Dispositivos Conectados - Sección directa */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
                  {/* Encabezado */}
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)" }}>
                        Dispositivos Conectados
                      </Typography>
                      <Typography sx={{ fontSize: "0.85rem", color: "hsl(240 3.8% 46.1%)" }}>
                        {(cwmpDeviceParams.params.devices?.length ?? 0) > 0
                          ? `${cwmpDeviceParams.params.devices!.filter((d) => d.active).length} dispositivos activos`
                          : "Sin dispositivos conectados"}
                      </Typography>
                    </Box>
                    {cwmpDeviceParams.params.devices && cwmpDeviceParams.params.devices.length > 0 && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Chip
                          label={`WiFi: ${cwmpDeviceParams.params.devices.filter((d) => d.active && d.interface === "802.11").length}`}
                          size="small"
                          sx={{
                            backgroundColor: "hsl(39 89% 40%)",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                        <Chip
                          label={`LAN: ${cwmpDeviceParams.params.devices.filter((d) => d.active && d.interface !== "802.11").length}`}
                          size="small"
                          sx={{
                            backgroundColor: "hsl(0 0% 40%)",
                            color: "white",
                            fontWeight: 600,
                          }}
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Contenido Dispositivos o Mensaje Vacío */}
                  {cwmpDeviceParams.params.devices && cwmpDeviceParams.params.devices.length === 0 ? (
                    <Card
                      sx={{
                        p: 3,
                        backgroundColor: "hsl(240 4.8% 97.9%)",
                        border: "2px dashed hsl(240 3.8% 74%)",
                        borderRadius: "12px",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1.5,
                        minHeight: "120px",
                      }}
                    >
                      <Wifi size={32} color="hsl(240 3.8% 60%)" />
                      <Box sx={{ textAlign: "center" }}>
                        <Typography sx={{ fontWeight: 600, color: "hsl(240 3.8% 46.1%)", mb: 0.5 }}>
                          No hay dispositivos conectados
                        </Typography>
                        <Typography sx={{ fontSize: "0.85rem", color: "hsl(240 3.8% 60%)" }}>
                          Conecta un dispositivo a través de WiFi o LAN para verlo aquí
                        </Typography>
                      </Box>
                    </Card>
                  ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                      {/* Sección WiFi */}
                      {cwmpDeviceParams.params.devices && cwmpDeviceParams.params.devices.filter((d) => d.interface === "802.11").length > 0 && (
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                backgroundColor: "hsl(39 89% 85%)",
                                borderRadius: "8px",
                              }}
                            >
                              <Wifi size={16} color="hsl(39 89% 40%)" />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", fontSize: "0.95rem" }}>
                              Conectados por WiFi
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {cwmpDeviceParams.params.devices
                              .filter((d) => d.interface === "802.11")
                              .map((device, index) => (
                                <Card
                                  key={`wifi-${index}`}
                                  sx={{
                                    p: 2,
                                    backgroundColor: device.active ? "hsl(0 0% 100%)" : "hsl(240 4.8% 97.9%)",
                                    border: `1px solid ${device.active ? "hsl(224.3 76.3% 85%)" : "hsl(240 4.8% 90%)"}`,
                                    borderRadius: "10px",
                                    opacity: device.active ? 1 : 0.6,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          color: device.active ? "hsl(240 10% 3.9%)" : "hsl(240 3.8% 60.1%)",
                                          mb: 0.5,
                                        }}
                                      >
                                        {device.name || "Dispositivo sin nombre"}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "0.85rem",
                                          color: device.active ? "hsl(240 3.8% 46.1%)" : "hsl(240 3.8% 65%)",
                                        }}
                                      >
                                        IP: {device.ip}
                                      </Typography>
                                    </Box>
                                    <Chip
                                      label={device.active ? "Activo" : "Inactivo"}
                                      size="small"
                                      sx={{
                                        backgroundColor: device.active ? "hsl(142 71.8% 70%)" : "hsl(240 3.8% 74%)",
                                        color: "white",
                                        fontWeight: 600,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Box>
                                </Card>
                              ))}
                          </Box>
                        </Box>
                      )}

                      {/* Sección LAN */}
                      {cwmpDeviceParams.params.devices && cwmpDeviceParams.params.devices.filter((d) => d.interface !== "802.11").length > 0 && (
                        <Box>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                width: 32,
                                height: 32,
                                backgroundColor: "hsl(0 0% 85%)",
                                borderRadius: "8px",
                              }}
                            >
                              <Wifi size={16} color="hsl(0 0% 40%)" />
                            </Box>
                            <Typography sx={{ fontWeight: 700, color: "hsl(240 10% 3.9%)", fontSize: "0.95rem" }}>
                              Conectados por LAN
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                            {cwmpDeviceParams.params.devices
                              .filter((d) => d.interface !== "802.11")
                              .map((device, index) => (
                                <Card
                                  key={`lan-${index}`}
                                  sx={{
                                    p: 2,
                                    backgroundColor: device.active ? "hsl(0 0% 100%)" : "hsl(240 4.8% 97.9%)",
                                    border: `1px solid ${device.active ? "hsl(224.3 76.3% 85%)" : "hsl(240 4.8% 90%)"}`,
                                    borderRadius: "10px",
                                    opacity: device.active ? 1 : 0.6,
                                  }}
                                >
                                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                    <Box sx={{ flex: 1 }}>
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          color: device.active ? "hsl(240 10% 3.9%)" : "hsl(240 3.8% 60.1%)",
                                          mb: 0.5,
                                        }}
                                      >
                                        {device.name || "Dispositivo sin nombre"}
                                      </Typography>
                                      <Typography
                                        sx={{
                                          fontSize: "0.85rem",
                                          color: device.active ? "hsl(240 3.8% 46.1%)" : "hsl(240 3.8% 65%)",
                                        }}
                                      >
                                        IP: {device.ip}
                                      </Typography>
                                    </Box>
                                    <Chip
                                      label={device.active ? "Activo" : "Inactivo"}
                                      size="small"
                                      sx={{
                                        backgroundColor: device.active ? "hsl(142 71.8% 70%)" : "hsl(240 3.8% 74%)",
                                        color: "white",
                                        fontWeight: 600,
                                        flexShrink: 0,
                                      }}
                                    />
                                  </Box>
                                </Card>
                              ))}
                          </Box>
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Card>
        </>
      )}

      {/* Modal de resultado (éxito/error) */}
      <Dialog
        open={resultModal.open}
        onClose={() => setResultModal({ ...resultModal, open: false })}
        PaperProps={{
          sx: {
            borderRadius: "16px",
            minWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            bgcolor: resultModal.type === "success" ? "hsl(142 71.8% 97%)" : "hsl(0 84.2% 97%)",
            borderBottom: `2px solid ${resultModal.type === "success" ? "hsl(142 71.8% 70%)" : "hsl(0 84.2% 70%)"}`,
            fontWeight: 700,
            color: resultModal.type === "success" ? "hsl(142 71.8% 30%)" : "hsl(0 84.2% 40%)",
          }}
        >
          {resultModal.type === "success" ? (
            <CheckCircle2 size={24} color="hsl(142 71.8% 45%)" />
          ) : (
            <AlertCircle size={24} color="hsl(0 84.2% 60.2%)" />
          )}
          {resultModal.type === "success" ? "Éxito" : "Error"}
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography sx={{ color: "hsl(240 3.8% 46.1%)", fontSize: "0.95rem", mb: 2 }}>
            {resultModal.message}
          </Typography>

          {resultModal.type === "success" && resultModal.savedParams && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
              {resultModal.savedParams.ssid2G && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "hsl(240 3.8% 60%)", textTransform: "uppercase" }}>
                    WiFi 2.4GHz
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1.5, borderLeft: "3px solid hsl(39 89% 70%)" }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)", mb: 0.3 }}>Red (SSID)</Typography>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "hsl(240 10% 3.9%)", wordBreak: "break-word" }}>
                        {resultModal.savedParams.ssid2G}
                      </Typography>
                    </Box>
                    {resultModal.savedParams.password2G && (
                      <Box>
                        <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)", mb: 0.3 }}>Contraseña</Typography>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "hsl(240 10% 3.9%)", fontFamily: "monospace", wordBreak: "break-word" }}>
                          {resultModal.savedParams.password2G}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}

              {resultModal.savedParams.ssid5G && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "hsl(240 3.8% 60%)", textTransform: "uppercase" }}>
                    WiFi 5GHz
                  </Typography>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pl: 1.5, borderLeft: "3px solid hsl(224.3 76.3% 70%)" }}>
                    <Box>
                      <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)", mb: 0.3 }}>Red (SSID)</Typography>
                      <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "hsl(240 10% 3.9%)", wordBreak: "break-word" }}>
                        {resultModal.savedParams.ssid5G}
                      </Typography>
                    </Box>
                    {resultModal.savedParams.password5G && (
                      <Box>
                        <Typography sx={{ fontSize: "0.75rem", color: "hsl(240 3.8% 60%)", mb: 0.3 }}>Contraseña</Typography>
                        <Typography sx={{ fontSize: "0.95rem", fontWeight: 600, color: "hsl(240 10% 3.9%)", fontFamily: "monospace", wordBreak: "break-word" }}>
                          {resultModal.savedParams.password5G}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, borderTop: "1px solid hsl(240 4.8% 95.9%)", justifyContent: "flex-end" }}>
          <Button
            onClick={() => setResultModal({ ...resultModal, open: false })}
            variant="contained"
            sx={{
              backgroundColor: resultModal.type === "success" ? "hsl(142 71.8% 45%)" : "hsl(0 84.2% 60.2%)",
              color: "white",
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor: resultModal.type === "success" ? "hsl(142 71.8% 40%)" : "hsl(0 84.2% 50%)",
              },
            }}
          >
            Aceptar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
